/**
 * 클릭 추적
 *
 * - 외부 링크(outbound) 클릭: `click` 이벤트
 * - 파일 다운로드 링크: `file_download` 이벤트
 * - 사이트 내 검색: `site_search` 이벤트
 */

import type { LambAnalytics } from '../core/sdk';

/** 다운로드 파일 확장자 목록 */
const DOWNLOAD_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'zip', 'rar', 'tar', 'gz',
];

/** 검색 쿼리 파라미터 키 */
const SEARCH_PARAMS = ['q', 'search', 'keyword'];

/** 클릭 추적기 */
export class ClickTracker {
  private sdk: LambAnalytics;
  private active = false;
  private trackOutbound: boolean;
  private trackFileDownload: boolean;
  private trackSiteSearch: boolean;
  private clickHandler: ((e: MouseEvent) => void) | null = null;

  constructor(
    sdk: LambAnalytics,
    options?: {
      outboundClick?: boolean;
      fileDownload?: boolean;
      siteSearch?: boolean;
    },
  ) {
    this.sdk = sdk;
    this.trackOutbound = options?.outboundClick !== false;
    this.trackFileDownload = options?.fileDownload !== false;
    this.trackSiteSearch = options?.siteSearch !== false;
  }

  start(): void {
    if (this.active || typeof document === 'undefined') return;
    this.active = true;

    this.clickHandler = (e: MouseEvent) => this.onClick(e);
    document.addEventListener('click', this.clickHandler, { capture: true });
  }

  stop(): void {
    if (!this.active || typeof document === 'undefined') return;
    this.active = false;

    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler, { capture: true });
      this.clickHandler = null;
    }
  }

  private onClick(e: MouseEvent): void {
    const anchor = this.findAnchorElement(e.target as Element | null);
    if (!anchor) return;

    const href = anchor.href;
    if (!href) return;

    let url: URL;
    try {
      url = new URL(href, window.location.origin);
    } catch {
      return;
    }

    if (this.trackFileDownload && this.isFileDownload(url)) {
      const fileName = this.getFileName(url);
      const fileExtension = this.getFileExtension(url);
      this.sdk.track('file_download', {
        file_name: fileName,
        file_extension: fileExtension,
        link_url: href,
      });
      return;
    }

    if (this.trackSiteSearch) {
      const searchTerm = this.getSearchTerm(url);
      if (searchTerm) {
        this.sdk.track('site_search', {
          search_term: searchTerm,
        });
        return;
      }
    }

    if (this.trackOutbound && this.isOutbound(url)) {
      this.sdk.track('click', {
        link_url: href,
        outbound: true,
      });
    }
  }

  private findAnchorElement(element: Element | null): HTMLAnchorElement | null {
    let current = element;
    while (current) {
      if (current.tagName === 'A') {
        return current as HTMLAnchorElement;
      }
      current = current.parentElement;
    }
    return null;
  }

  private isOutbound(url: URL): boolean {
    if (typeof window === 'undefined') return false;
    return url.hostname !== window.location.hostname;
  }

  private isFileDownload(url: URL): boolean {
    const ext = this.getFileExtension(url);
    return ext !== '' && DOWNLOAD_EXTENSIONS.includes(ext);
  }

  private getFileExtension(url: URL): string {
    const pathname = url.pathname;
    const lastDot = pathname.lastIndexOf('.');
    if (lastDot === -1) return '';
    return pathname.substring(lastDot + 1).toLowerCase();
  }

  private getFileName(url: URL): string {
    const pathname = url.pathname;
    const lastSlash = pathname.lastIndexOf('/');
    return lastSlash === -1 ? pathname : pathname.substring(lastSlash + 1);
  }

  private getSearchTerm(url: URL): string | null {
    for (const param of SEARCH_PARAMS) {
      const value = url.searchParams.get(param);
      if (value) return value;
    }
    return null;
  }
}
